import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CraneDetailsForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    // Basic Information
    make: '',
    model: '',
    year: '',
    serialNumber: '',
    hours: '',
    
    // Technical Specifications
    craneType: '',
    capacity: '',
    boomLength: '',
    engineMake: '',
    engineModel: '',
    transmission: '',
    
    // Condition Assessment
    condition: '',
    maintenanceHistory: '',
    recentRepairs: '',
    modifications: '',
    
    // Location & Context
    currentLocation: '',
    operatingEnvironment: '',
    primaryUse: '',
    
    // Additional Details
    paintCondition: '',
    tireCondition: '',
    hydraulicSystem: '',
    electricalSystem: '',
    structuralIntegrity: '',
    
    // Optional Information
    purchasePrice: '',
    purchaseDate: '',
    maintenanceRecords: false,
    inspectionCertificates: false,
    operatorManuals: false
  });

  const [errors, setErrors] = useState({});
  const [currentSection, setCurrentSection] = useState(1);

  const craneTypes = [
    'Mobile Crane',
    'Rough Terrain',
    'All Terrain',
    'Crawler Crane',
    'Tower Crane',
    'Truck Mounted',
    'Carry Deck',
    'Pick and Carry'
  ];

  const makes = [
    'Liebherr', 'Grove', 'Manitowoc', 'Terex', 'Tadano', 'Link-Belt',
    'Kobelco', 'Sany', 'XCMG', 'Zoomlion', 'Demag', 'Krupp'
  ];

  const conditionOptions = [
    { value: '5', label: 'Excellent - Like new, minimal wear' },
    { value: '4', label: 'Good - Well maintained, normal wear' },
    { value: '3', label: 'Fair - Some wear, functioning well' },
    { value: '2', label: 'Poor - Significant wear, needs attention' },
    { value: '1', label: 'Salvage - Major issues, parts value only' }
  ];

  const environmentOptions = [
    'Construction Sites',
    'Industrial Facilities',
    'Marine/Port Operations',
    'Oil & Gas',
    'Mining Operations',
    'Urban Development',
    'Infrastructure Projects',
    'Mixed Use'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData?.make) newErrors.make = 'Make is required';
    if (!formData?.model) newErrors.model = 'Model is required';
    if (!formData?.year) newErrors.year = 'Year is required';
    if (!formData?.craneType) newErrors.craneType = 'Crane type is required';
    if (!formData?.capacity) newErrors.capacity = 'Capacity is required';
    if (!formData?.condition) newErrors.condition = 'Condition is required';
    
    // Numeric validations
    if (formData?.year && (formData?.year < 1990 || formData?.year > 2025)) {
      newErrors.year = 'Year must be between 1990 and 2025';
    }
    if (formData?.hours && formData?.hours < 0) {
      newErrors.hours = 'Hours cannot be negative';
    }
    if (formData?.capacity && formData?.capacity <= 0) {
      newErrors.capacity = 'Capacity must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const sections = [
    { id: 1, title: 'Basic Information', icon: 'FileText' },
    { id: 2, title: 'Technical Specs', icon: 'Settings' },
    { id: 3, title: 'Condition Assessment', icon: 'CheckCircle' },
    { id: 4, title: 'Additional Details', icon: 'Info' }
  ];

  const renderBasicInformation = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Make *
          </label>
          <Select
            value={formData?.make}
            onChange={(value) => handleInputChange('make', value)}
            options={makes?.map(make => ({ value: make, label: make }))}
            placeholder="Select crane make"
            error={errors?.make}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Model *
          </label>
          <Input
            type="text"
            value={formData?.model}
            onChange={(e) => handleInputChange('model', e?.target?.value)}
            placeholder="Enter model number"
            error={errors?.model}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Year *
          </label>
          <Input
            type="number"
            value={formData?.year}
            onChange={(e) => handleInputChange('year', e?.target?.value)}
            placeholder="e.g., 2020"
            min="1990"
            max="2025"
            error={errors?.year}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Serial Number
          </label>
          <Input
            type="text"
            value={formData?.serialNumber}
            onChange={(e) => handleInputChange('serialNumber', e?.target?.value)}
            placeholder="Enter serial number"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Hours of Operation
          </label>
          <Input
            type="number"
            value={formData?.hours}
            onChange={(e) => handleInputChange('hours', e?.target?.value)}
            placeholder="e.g., 2500"
            min="0"
            error={errors?.hours}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Current Location
        </label>
        <Input
          type="text"
          value={formData?.currentLocation}
          onChange={(e) => handleInputChange('currentLocation', e?.target?.value)}
          placeholder="City, State or Region"
        />
      </div>
    </div>
  );

  const renderTechnicalSpecs = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Crane Type *
          </label>
          <Select
            value={formData?.craneType}
            onChange={(value) => handleInputChange('craneType', value)}
            options={craneTypes?.map(type => ({ value: type, label: type }))}
            placeholder="Select crane type"
            error={errors?.craneType}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Lifting Capacity (tons) *
          </label>
          <Input
            type="number"
            value={formData?.capacity}
            onChange={(e) => handleInputChange('capacity', e?.target?.value)}
            placeholder="e.g., 130"
            min="1"
            error={errors?.capacity}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Main Boom Length (ft)
          </label>
          <Input
            type="number"
            value={formData?.boomLength}
            onChange={(e) => handleInputChange('boomLength', e?.target?.value)}
            placeholder="e.g., 60"
            min="10"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Transmission Type
          </label>
          <Select
            value={formData?.transmission}
            onChange={(value) => handleInputChange('transmission', value)}
            options={[
              { value: 'Manual', label: 'Manual' },
              { value: 'Automatic', label: 'Automatic' },
              { value: 'Powershift', label: 'Powershift' },
              { value: 'CVT', label: 'CVT' }
            ]}
            placeholder="Select transmission"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Engine Make
          </label>
          <Input
            type="text"
            value={formData?.engineMake}
            onChange={(e) => handleInputChange('engineMake', e?.target?.value)}
            placeholder="e.g., Mercedes, Caterpillar"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Engine Model
          </label>
          <Input
            type="text"
            value={formData?.engineModel}
            onChange={(e) => handleInputChange('engineModel', e?.target?.value)}
            placeholder="e.g., OM906LA"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Operating Environment
        </label>
        <Select
          value={formData?.operatingEnvironment}
          onChange={(value) => handleInputChange('operatingEnvironment', value)}
          options={environmentOptions?.map(env => ({ value: env, label: env }))}
          placeholder="Select primary operating environment"
        />
      </div>
    </div>
  );

  const renderConditionAssessment = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Overall Condition *
        </label>
        <Select
          value={formData?.condition}
          onChange={(value) => handleInputChange('condition', value)}
          options={conditionOptions}
          placeholder="Select overall condition"
          error={errors?.condition}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Paint Condition
          </label>
          <Select
            value={formData?.paintCondition}
            onChange={(value) => handleInputChange('paintCondition', value)}
            options={[
              { value: 'Excellent', label: 'Excellent - Like new' },
              { value: 'Good', label: 'Good - Minor touch-ups needed' },
              { value: 'Fair', label: 'Fair - Noticeable fading/scratches' },
              { value: 'Poor', label: 'Poor - Significant rust/damage' }
            ]}
            placeholder="Select paint condition"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Tire/Track Condition
          </label>
          <Select
            value={formData?.tireCondition}
            onChange={(value) => handleInputChange('tireCondition', value)}
            options={[
              { value: 'Excellent', label: 'Excellent - Like new' },
              { value: 'Good', label: 'Good - 70%+ tread remaining' },
              { value: 'Fair', label: 'Fair - 40-70% tread remaining' },
              { value: 'Poor', label: 'Poor - Replacement needed soon' }
            ]}
            placeholder="Select tire/track condition"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Hydraulic System
          </label>
          <Select
            value={formData?.hydraulicSystem}
            onChange={(value) => handleInputChange('hydraulicSystem', value)}
            options={[
              { value: 'Excellent', label: 'Excellent - No leaks, smooth operation' },
              { value: 'Good', label: 'Good - Minor seepage, functions well' },
              { value: 'Fair', label: 'Fair - Some leaks, adequate performance' },
              { value: 'Poor', label: 'Poor - Major leaks, erratic operation' }
            ]}
            placeholder="Select hydraulic condition"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Structural Integrity
          </label>
          <Select
            value={formData?.structuralIntegrity}
            onChange={(value) => handleInputChange('structuralIntegrity', value)}
            options={[
              { value: 'Excellent', label: 'Excellent - No visible damage' },
              { value: 'Good', label: 'Good - Minor wear, structurally sound' },
              { value: 'Fair', label: 'Fair - Some damage, still operational' },
              { value: 'Poor', label: 'Poor - Significant damage, needs repair' }
            ]}
            placeholder="Select structural condition"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Recent Repairs & Maintenance
        </label>
        <textarea
          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          rows="3"
          value={formData?.recentRepairs}
          onChange={(e) => handleInputChange('recentRepairs', e?.target?.value)}
          placeholder="Describe any recent repairs, maintenance, or upgrades..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Modifications & Attachments
        </label>
        <textarea
          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          rows="3"
          value={formData?.modifications}
          onChange={(e) => handleInputChange('modifications', e?.target?.value)}
          placeholder="List any modifications, special attachments, or non-standard features..."
        />
      </div>
    </div>
  );

  const renderAdditionalDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Original Purchase Price
          </label>
          <Input
            type="number"
            value={formData?.purchasePrice}
            onChange={(e) => handleInputChange('purchasePrice', e?.target?.value)}
            placeholder="e.g., 1250000"
            min="0"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Purchase Date
          </label>
          <Input
            type="date"
            value={formData?.purchaseDate}
            onChange={(e) => handleInputChange('purchaseDate', e?.target?.value)}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Primary Use/Application
        </label>
        <Select
          value={formData?.primaryUse}
          onChange={(value) => handleInputChange('primaryUse', value)}
          options={[
            { value: 'General Construction', label: 'General Construction' },
            { value: 'Heavy Industrial', label: 'Heavy Industrial' },
            { value: 'Infrastructure', label: 'Infrastructure Projects' },
            { value: 'Maintenance', label: 'Maintenance & Repair' },
            { value: 'Material Handling', label: 'Material Handling' },
            { value: 'Emergency Services', label: 'Emergency Services' },
            { value: 'Specialty Applications', label: 'Specialty Applications' }
          ]}
          placeholder="Select primary use"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-4">
          Available Documentation
        </label>
        <div className="space-y-3">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData?.maintenanceRecords}
              onChange={(e) => handleInputChange('maintenanceRecords', e?.target?.checked)}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            <span className="text-sm text-text-primary">Complete maintenance records available</span>
          </label>
          
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData?.inspectionCertificates}
              onChange={(e) => handleInputChange('inspectionCertificates', e?.target?.checked)}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            <span className="text-sm text-text-primary">Current inspection certificates</span>
          </label>
          
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData?.operatorManuals}
              onChange={(e) => handleInputChange('operatorManuals', e?.target?.checked)}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            <span className="text-sm text-text-primary">Original operator manuals and documentation</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Maintenance History Notes
        </label>
        <textarea
          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          rows="4"
          value={formData?.maintenanceHistory}
          onChange={(e) => handleInputChange('maintenanceHistory', e?.target?.value)}
          placeholder="Provide details about maintenance schedule, service history, any known issues..."
        />
      </div>
    </div>
  );

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card">
      {/* Section Navigation */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">Crane Details</h2>
          <span className="text-sm text-text-secondary">
            Section {currentSection} of {sections?.length}
          </span>
        </div>
        
        <div className="flex space-x-1 overflow-x-auto">
          {sections?.map((section) => (
            <button
              key={section?.id}
              onClick={() => setCurrentSection(section?.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                currentSection === section?.id
                  ? 'bg-primary text-white' :'bg-muted text-text-secondary hover:bg-muted-hover hover:text-text-primary'
              }`}
            >
              <Icon name={section?.icon} size={16} />
              <span>{section?.title}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Form Content */}
      <form onSubmit={handleSubmit} className="p-6">
        {currentSection === 1 && renderBasicInformation()}
        {currentSection === 2 && renderTechnicalSpecs()}
        {currentSection === 3 && renderConditionAssessment()}
        {currentSection === 4 && renderAdditionalDetails()}

        {/* Navigation and Submit */}
        <div className="flex items-center justify-between pt-6 mt-6 border-t border-border">
          <div className="flex space-x-3">
            {currentSection > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentSection(currentSection - 1)}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>
            )}
          </div>
          
          <div className="flex space-x-3">
            {currentSection < sections?.length ? (
              <Button
                type="button"
                onClick={() => setCurrentSection(currentSection + 1)}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next Section
              </Button>
            ) : (
              <Button
                type="submit"
                isLoading={isLoading}
                iconName="Calculator"
                iconPosition="left"
              >
                {isLoading ? 'Calculating Valuation...' : 'Get Valuation'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CraneDetailsForm;